<script lang="ts" setup>
    import { onMounted, reactive, ref, nextTick, computed } from 'vue';
    import '../../../css/choice.css';
    import PageTitle from '../../components/PageTitle.vue';
    import TextInput from '../../components/TextInput.vue';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import { ActionButtonStyle } from '../../lib/Types/Types';
    import { DisplayStyle, ButtonDisplayStyle, IconTypes, ModalSize } from '../../Enums/UiEnums';
    import * as choiceApi from '../../lib/ChoiceApi';
    import { IFishboneType, IFishboneSubType, IFishboneOrder, IFishboneOrderLocation, IFishboneOrderResult, IFishboneCreatePayload, ResultCodeTypes, IFishboneResultCodeType, IFishboneOrderResultParsed } from '../../lib/Models/Fishbones';
    import Checkbox from '../../components/Inputs/Checkbox.vue';
    import TabContainer from '../../components/Section/Tabs/TabContainer.vue';
    import TabItem from '../../components/Section/Tabs/TabItem.vue';
    import vSelect from 'vue-select';
    import { confirmDialog, ModalStyles, popAlert } from '../../lib/Notification';
    import Icon from '../../components/Icon.vue';
    import { IToolbar, ToolbarSize, ToolbarStyle, ToolbarBackground } from '../../lib/Toolbar';
    import Toolbar from '../../components/Toolbar.vue';
    import ModalDialog from '../../components/ModalDialog.vue';
    import { VueDraggable } from 'vue-draggable-plus';

    enum TabType {
        enabled,
        archived
    }

    let pageData = reactive<{
        fbTypes?: IFishboneType[],
        selectedFishbone?: IFishboneType,
        fbSearch?: string,
        fbSubTypes?: IFishboneSubType[],
        selectedFbSubTypes?: IFishboneSubType[],
        selectedSubType?: IFishboneSubType,
        fbSubSearch?: string,
        fbOrders?: IFishboneOrder[],
        selectedFbOrders?: IFishboneOrder[],
        selectedOrder?: IFishboneOrder,
        labSearch?: string,
        codeSearch?: string,
        fbOrderLocations?: IFishboneOrderLocation[],
        fbOrderResults?: IFishboneOrderResult[],
        selectedFbTab: TabType,
        selectedFbSubTypeTab: TabType,
        selectedFbOrderTab: TabType,
    }>({
        fbTypes: undefined,
        selectedFishbone: undefined,
        fbSearch: undefined,
        fbSubTypes: undefined,
        selectedSubType: undefined,
        fbSubSearch: undefined,
        fbOrders: undefined,
        selectedOrder: undefined,
        labSearch: undefined,
        codeSearch: undefined,
        fbOrderLocations: undefined,
        fbOrderResults: undefined,
        selectedFbTab: TabType.enabled,
        selectedFbSubTypeTab: TabType.enabled,
        selectedFbOrderTab: TabType.enabled,
    });

    let editState = reactive<{
        isEditMode: boolean,
        fbTypeArchiveToggle: Record<number, IFishboneType>,
        fbSubTypeArchiveToggle: Record<string, IFishboneSubType>,
        fbOrderArchiveToggle: Record<string, IFishboneOrder>,
        newFishboneName?: string,
        fishboneItemLabelToCreate: string[],
        fishboneItemDescriptionToCreate: string[],
        fishboneOrderLabToCreate: string[],
        fishboneOrderCodeToCreate: string[],
        fishboneResultTypeToCreate: string[],
        fishboneResultCodeToCreate: string[],
        fishboneCopyName?: string
    }>({
        isEditMode: false,
        fbTypeArchiveToggle: {},
        fbSubTypeArchiveToggle: {},
        fbOrderArchiveToggle: {},
        newFishboneName: undefined,
        fishboneItemLabelToCreate: [],
        fishboneItemDescriptionToCreate: [],
        fishboneOrderLabToCreate: [],
        fishboneOrderCodeToCreate: [],
        fishboneResultTypeToCreate: [],
        fishboneResultCodeToCreate: [],
        fishboneCopyName: undefined
    });

    const newFishboneInput = ref();

    enum ToolbarIdentifier {
        refresh, 
        save,
        setup,
        copy
    }

    const toolbarConfiguration = ref<IToolbar>(
        {
            toolbarList: [
                {identifier: ToolbarIdentifier.refresh, action: ActionButtonStyle.Refresh, tooltip: "Refresh fishbone list", width: 95, performAction: getAllFishboneData},
                {identifier: ToolbarIdentifier.save, action: ActionButtonStyle.Save, tooltip: "Save fishbone changes", width: 95, enabled: editState.isEditMode, performAction: saveFishboneChanges},
                {identifier: ToolbarIdentifier.setup, label: "Setup", tooltip: "Setup wireframe", action: ActionButtonStyle.Tools, width: 95, enabled: editState.isEditMode, performAction: openWireframeSetup},
                {identifier: ToolbarIdentifier.copy, label: "Copy", tooltip: "Copy fishbone", action: ActionButtonStyle.Copy, width: 95, enabled: editState.isEditMode, performAction: openFishboneCopyDialogue }
            ]
        }
    );

    const allFishbonesSelectedForToggle = computed<boolean>(() => Object.keys(editState.fbTypeArchiveToggle).length == pageData.fbTypes?.length);
    const allFishboneSubTypesSelectedForToggle = computed<boolean>(() => Object.keys(editState.fbSubTypeArchiveToggle).length == pageData.fbSubTypes?.length);
    const allFishboneOrdersSelectedForToggle = computed<boolean>(() => Object.keys(editState.fbOrderArchiveToggle).length == pageData.fbOrders?.length);

    onMounted(async () => {
        getAllFishboneData();
    });

    async function getAllFishboneData() {
        const [fbTypes, fbSubTypes, fbOrders, fbOrderLocations, fbOrderResults] = await Promise.all([
            choiceApi.fishbones.getFishboneTypes(),
            choiceApi.fishbones.getFishboneSubTypes(),
            choiceApi.fishbones.getFishboneOrders(),
            choiceApi.fishbones.getFishboneOrderLocations(),
            choiceApi.fishbones.getFishboneOrderResults()
        ]);

        // Assign the results to pageData
        pageData.fbTypes = fbTypes;
        pageData.fbSubTypes = fbSubTypes;
        pageData.fbOrders = fbOrders;
        pageData.fbOrderLocations = fbOrderLocations;
        pageData.fbOrderResults = fbOrderResults;

        if (pageData.selectedFishbone) {
            updateViewedLists();
        }
    }

    function clearOnSearch(section: string) {
        if (section == 'fb') {
            pageData.selectedOrder = undefined;
            pageData.selectedSubType = undefined;
            pageData.selectedFishbone = undefined;
            updateViewedLists();
        }
        else if (section == 'subFb') {
            pageData.selectedOrder = undefined;
            pageData.selectedSubType = undefined;
        }
        else if (section == 'order') {
            pageData.selectedOrder = undefined;
        }
    }

    async function openWireframeSetup() {
        if (!pageData.selectedFishbone) {
            popAlert("Please select a fishbone to connect the wireframe to.", "Wireframe Setup", ModalStyles.info);
            return;
        }
        const screenWidth = window.screen.width;
        const screenHeight = window.screen.height;
        const left = (screenWidth - 800) / 2;
        const top = (screenHeight - 400) / 2;
        const window_features = `popup=yes,resizable=yes,width=860,height=460,top=${top},left=${left}`;
        window.open(`/officemd/App/setup/fishbones/wireframesetup?fbId=${pageData.selectedFishbone?.fbId}`,"wireframe_setup",window_features);
    } 

    function selectFishbone(fishbone: IFishboneType) {
        pageData.selectedFishbone = fishbone;
        // reset relevant pageData
        pageData.selectedSubType = undefined;
        pageData.selectedOrder = undefined;
        updateViewedLists();

        // reset editState
        editState.newFishboneName = undefined;
        resetItemAndOrderEditState();
        editState.fishboneResultTypeToCreate = [];
        editState.fishboneResultCodeToCreate = [];
    }
    
    function selectFishboneSubType(fishboneSubType: IFishboneSubType) {
        pageData.selectedSubType = fishboneSubType;
        pageData.selectedOrder = undefined;
        editState.fishboneResultTypeToCreate = [];
        editState.fishboneResultCodeToCreate = [];
    }

    async function selectOrder(order: IFishboneOrder) {
        if (pageData.selectedSubType) {
            pageData.selectedOrder = order;
            editState.fishboneResultTypeToCreate = [];
            editState.fishboneResultCodeToCreate = [];
        }
    }

    function getOrderLocationDisplayName(order: IFishboneOrder) {
        return pageData.fbOrderLocations?.find(orderLocation => orderLocation.locationId == order.orderLocationId);
    }

    function getOrderLocationDisplayNameByResult(orderId: number) {
        const order = pageData.fbOrders?.find(order => order.orderId == orderId);
        return pageData.fbOrderLocations?.find(orderLocation => orderLocation.locationId == order?.orderLocationId)?.displayName;
    }

    function displayOrder(order: IFishboneOrder) {
        return (
            (!pageData.labSearch && !pageData.codeSearch) || 
            (pageData.labSearch && new RegExp(pageData.labSearch,'i').test(getOrderLocationDisplayName(order)?.displayName ?? '') && !pageData.codeSearch) ||
            (pageData.codeSearch && new RegExp(pageData.codeSearch!,'i').test(order.orderCode) && !pageData.labSearch) ||
            (pageData.labSearch && new RegExp(pageData.labSearch,'i').test(getOrderLocationDisplayName(order)?.displayName ?? '') && pageData.codeSearch && new RegExp(pageData.codeSearch!,'i').test(order.orderCode))
        )
    }

    function toggleEditMode() {
        editState.isEditMode = !editState.isEditMode;
        pageData.selectedOrder = undefined;
        pageData.selectedSubType = undefined;
        pageData.selectedFishbone = undefined;
        updateViewedLists();
        editState.fbTypeArchiveToggle = {};
        editState.fbSubTypeArchiveToggle = {};
        editState.fbOrderArchiveToggle = {};
        editState.newFishboneName = undefined;
        resetItemAndOrderEditState();
        editState.fishboneResultTypeToCreate = [];
        editState.fishboneResultCodeToCreate = [];
        toolbarConfiguration.value.toolbarList[ToolbarIdentifier.save].enabled = editState.isEditMode;
        toolbarConfiguration.value.toolbarList[ToolbarIdentifier.setup].enabled = editState.isEditMode;
        toolbarConfiguration.value.toolbarList[ToolbarIdentifier.copy].enabled = editState.isEditMode;
    }

    function toggleFbArchiveStatus(item: IFishboneType) {
        if (!editState.fbTypeArchiveToggle[item.fbId])
            editState.fbTypeArchiveToggle[item.fbId] = item;
        else
            delete editState.fbTypeArchiveToggle[item.fbId];
    }

    function toggleAllEnabledFbArchiveState(checked: boolean) : void {
        if (checked)
            pageData.fbTypes?.
                filter(fb => fb.archived == false).
                forEach(fb => editState.fbTypeArchiveToggle[fb.fbId] = fb);
        else
            editState.fbTypeArchiveToggle = {};
    }

    function toggleAllArchivedFbArchiveState(checked: boolean) : void {
        if (checked)
            pageData.fbTypes?.
                filter(fb => fb.archived == true).
                forEach(fb => editState.fbTypeArchiveToggle[fb.fbId] = fb);
        else
            editState.fbTypeArchiveToggle = {};
    }

    function toggleFbSubTypeArchiveStatus(item: IFishboneSubType) {
        if (!editState.fbSubTypeArchiveToggle[item.fbSubId])
            editState.fbSubTypeArchiveToggle[item.fbSubId] = item;
        else
            delete editState.fbSubTypeArchiveToggle[item.fbSubId];
    }

    function toggleAllEnabledFbSubTypeArchiveState(checked: boolean) : void {
        if (checked)
            pageData.fbSubTypes?.
                filter(fbSubType => fbSubType.archived == false).
                forEach(fbSubType => editState.fbSubTypeArchiveToggle[fbSubType.fbSubId] = fbSubType);
        else
            editState.fbSubTypeArchiveToggle = {};
    }

    function toggleAllArchivedFbSubTypeArchiveState(checked: boolean) : void {
        if (checked)
            pageData.fbSubTypes?.
                filter(fbSubType => fbSubType.archived == true).
                forEach(fbSubType => editState.fbSubTypeArchiveToggle[fbSubType.fbSubId] = fbSubType);
        else
            editState.fbSubTypeArchiveToggle = {};
    }

    function toggleFbOrderArchiveStatus(item: IFishboneOrder) {
        if (!editState.fbOrderArchiveToggle[item.orderId])
            editState.fbOrderArchiveToggle[item.orderId] = item;
        else
            delete editState.fbOrderArchiveToggle[item.orderId];
    }

    function toggleAllEnabledFbOrderArchiveStatus(checked: boolean) : void {
        if (checked)
            pageData.fbOrders?.
                filter(fbOrder => fbOrder.archived == false).
                forEach(fbOrder => editState.fbOrderArchiveToggle[fbOrder.orderId] = fbOrder);
        else
            editState.fbOrderArchiveToggle = {};
    }

    function toggleAllArchivedFbOrderArchiveStatus(checked: boolean) : void {
        if (checked)
            pageData.fbOrders?.
                filter(fbOrder => fbOrder.archived == true).
                forEach(fbOrder => editState.fbOrderArchiveToggle[fbOrder.orderId] = fbOrder);
        else
            editState.fbOrderArchiveToggle = {};
    }

    function clearFbToggleList() {
        editState.fbTypeArchiveToggle = {};
        editState.newFishboneName = undefined;
    }
    function clearFbSubTypeToggleList() {
        editState.fbSubTypeArchiveToggle = {};
    }
    function clearFbOrderToggleList() {
        editState.fbOrderArchiveToggle = {};
    }

    async function submitArchiveFishboneToggle() {
        let fishbones = Object.values(editState.fbTypeArchiveToggle);
        for (const fb of fishbones) {
            fb.archived = !fb.archived;
        }
        editState.fbTypeArchiveToggle = {};
        const fbTypes = await choiceApi.fishbones.updateFishbones(fishbones);
        if (fbTypes) {
            pageData.fbTypes = fbTypes;
        }
        updateViewedLists();
    }

    async function submitArchiveFishboneSubTypeToggle() {
        let fishboneSubTypes = Object.values(editState.fbSubTypeArchiveToggle);
        for (const fbSubType of fishboneSubTypes) {
            fbSubType.archived = !fbSubType.archived;
        }
        editState.fbSubTypeArchiveToggle = {};
        const fbSubTypes = await choiceApi.fishbones.updateFishboneSubTypes(fishboneSubTypes);
        if (fbSubTypes) {
            pageData.fbSubTypes = fbSubTypes;
        }
        updateViewedLists();
    }

    async function submitArchiveFishboneOrderToggle() {
        let fishboneOrders = Object.values(editState.fbOrderArchiveToggle);
        for (const fbOrder of fishboneOrders) {
            fbOrder.archived = !fbOrder.archived;
        }
        editState.fbOrderArchiveToggle = {};
        const fbOrders = await choiceApi.fishbones.updateFishboneOrders(fishboneOrders);
        if (fbOrders) {
            pageData.fbOrders = fbOrders;
        }
        updateViewedLists();
    }

    async function createFishbone(){
        pageData.selectedFishbone = undefined;
        updateViewedLists();
        editState.newFishboneName = "";
        // Wait for the DOM to update
        await nextTick();
        newFishboneInput.value.focus();
    }

    function buildCreationPayload() {
        let fishboneCreatePayload: IFishboneCreatePayload = {};
            if (editState.newFishboneName) {
                let fbCount = pageData.fbTypes?.length ?? 0;
                fishboneCreatePayload.fishbone = {
                    description: editState.newFishboneName,
                    archived: false,
                    sortOrder: ++fbCount
                };
            }
            if (editState.fishboneItemLabelToCreate.length) {
                let fbItemCount = pageData.fbSubTypes?.length ?? 0;
                fishboneCreatePayload.fishboneItems = [];
                editState.fishboneItemLabelToCreate.forEach((element, index) => {
                    if (fishboneCreatePayload.fishboneItems) {
                        fishboneCreatePayload.fishboneItems.push(
                            {
                                fbId: pageData.selectedFishbone?.fbId,
                                label: element,
                                description: editState.fishboneItemDescriptionToCreate[index],
                                archived: false,
                                sortOrder: ++fbItemCount
                            }
                        )
                    }
                });
            }
            if (editState.fishboneOrderLabToCreate.length) {
                let fbOrderCount = pageData.fbOrders?.length ?? 0;
                fishboneCreatePayload.fishboneOrders = [];
                editState.fishboneOrderLabToCreate.forEach((element, index) => {
                    if (fishboneCreatePayload.fishboneOrders) {
                        fishboneCreatePayload.fishboneOrders.push(
                            {
                                fbId: pageData.selectedFishbone?.fbId,
                                orderLocationId: Number(element),
                                orderCode: editState.fishboneOrderCodeToCreate[index],
                                archived: false,
                                sortOrder: ++fbOrderCount
                            }
                        )
                    }
                });
            }
            if (editState.fishboneResultTypeToCreate.length) {
                fishboneCreatePayload.fishboneResults = [];
                editState.fishboneResultTypeToCreate.forEach((element, index) => {
                    if (fishboneCreatePayload.fishboneResults && pageData.selectedOrder && pageData.selectedSubType) {
                        fishboneCreatePayload.fishboneResults.push(
                            {
                                orderId: pageData.selectedOrder.orderId,
                                fbSubId: pageData.selectedSubType.fbSubId,
                                fbResultCodeType: element,
                                fbResultCode: editState.fishboneResultCodeToCreate[index]
                            }
                        )
                    }
                });
            }
        return fishboneCreatePayload;
    }

    async function updateSortOrders() {
        pageData.fbTypes?.forEach((fb, index) => fb.sortOrder = index);
        pageData.selectedFbSubTypes?.forEach((item, index) => item.sortOrder = index);
        pageData.selectedFbOrders?.forEach((order, index) => order.sortOrder = index);

        const [fbTypes, fbSubTypes, fbOrders] = await Promise.all([
            choiceApi.fishbones.updateFishbones(pageData.fbTypes ?? []),
            choiceApi.fishbones.updateFishboneSubTypes(pageData.selectedFbSubTypes ?? []),
            choiceApi.fishbones.updateFishboneOrders(pageData.selectedFbOrders ?? [])
        ]);

        // Assign the updated data to pageData
        pageData.fbTypes = fbTypes;
        pageData.fbSubTypes = fbSubTypes;
        pageData.fbOrders = fbOrders;
    }

    async function saveFishboneChanges() {
        // creation
        if (editState.newFishboneName || 
            editState.fishboneItemLabelToCreate.length ||
            editState.fishboneOrderLabToCreate.length ||
            editState.fishboneResultTypeToCreate.length
        ) {
            let fishboneCreatePayload = buildCreationPayload();
            const result = await choiceApi.fishbones.createFishbone(fishboneCreatePayload);
            if (result) { // error
                popAlert(result, "Fishbone Create", ModalStyles.warning);
            }

            resetItemAndOrderEditState();
            await getAllFishboneData();
          
            editState.fishboneResultTypeToCreate = [];
            editState.fishboneResultCodeToCreate = [];

            if (!pageData.selectedFishbone) {
                pageData.selectedFishbone = pageData.fbTypes?.find(fb => fb.description == editState.newFishboneName);
                updateViewedLists();
                editState.newFishboneName = undefined;
            }
        }

        // update sort orders
        await updateSortOrders();

        // reset edit state
        editState.newFishboneName = undefined;
        resetItemAndOrderEditState();
    }

    function resetItemAndOrderEditState() {
        editState.fishboneItemLabelToCreate = [];
        editState.fishboneItemDescriptionToCreate = [];
        editState.fishboneOrderLabToCreate = [];
        editState.fishboneOrderCodeToCreate = [];
    }

    async function addItem() {
        editState.fishboneItemLabelToCreate.push("");
        editState.fishboneItemDescriptionToCreate.push("");
        await nextTick();
        document.getElementById("fbSubType_" + (editState.fishboneItemLabelToCreate.length-1))?.scrollIntoView();
    }

    async function addOrder() {
        editState.fishboneOrderLabToCreate.push("");
        editState.fishboneOrderCodeToCreate.push("");
        await nextTick();
        document.getElementById("fbOrder_" + (editState.fishboneOrderLabToCreate.length-1))?.scrollIntoView();
    }

    async function addResult() {
        editState.fishboneResultTypeToCreate.push("");
        editState.fishboneResultCodeToCreate.push("");
        await nextTick();
        document.getElementById("result_" + (editState.fishboneResultCodeToCreate.length-1))?.scrollIntoView();
    }

    async function displayDeleteWarning(fishboneResult: IFishboneOrderResult) {
        const result = await confirmDialog(
            "Are you sure you want to delete this item? This command will take effect immediately.",
            { 
                titleHTML: "Fishbone result delete",
                ConfirmText: "Delete",
                RejectText: "Cancel",
                modalStyle: ModalStyles.danger
            }
        );
        if (result) {
            const fbOrderResults = await choiceApi.fishbones.deleteFishboneResult(fishboneResult);
            if (fbOrderResults) {
                pageData.fbOrderResults = fbOrderResults;
            }
        }
    }

    async function openFishboneCopyDialogue() {
        if (!pageData.selectedFishbone) {
            popAlert("Please select a fishbone to copy.", "Fishbone Copy", ModalStyles.info);
            return;
        }
        editState.fishboneCopyName = ""
    }

    async function createFishboneCopy() {
        const selectedFbId = pageData.selectedFishbone?.fbId;
        let fishboneCreatePayload: IFishboneCreatePayload = {};
        if (!editState.fishboneCopyName?.length) {
            popAlert("Fishbone name must be entered.", "Fishbone Copy", ModalStyles.info);
            editState.fishboneCopyName = undefined;
            return;
        }

        fishboneCreatePayload.fishbone = {
            description: editState.fishboneCopyName,
            archived: false,
            sortOrder: pageData.selectedFishbone?.sortOrder ?? 0
        };

        // copy fishone items
        fishboneCreatePayload.fishboneItems = [];
        pageData.fbSubTypes?.filter(fbItem => fbItem.fbId == selectedFbId).forEach(
            (fbItem) => {
                fishboneCreatePayload.fishboneItems?.push(
                    {
                        fbId: pageData.selectedFishbone?.fbId,
                        label: fbItem.label,
                        description: fbItem.description,
                        archived: fbItem.archived,
                        sortOrder: fbItem.sortOrder
                    }
                )
            }
        );

        // copy fishone orders
        fishboneCreatePayload.fishboneOrders = [];
        pageData.fbOrders?.filter(fbOrder => fbOrder.fbId == selectedFbId).forEach(
            (fbOrder) => {
                fishboneCreatePayload.fishboneOrders?.push(
                    {
                        fbId: pageData.selectedFishbone?.fbId,
                        orderLocationId: fbOrder.orderLocationId,
                        orderCode: fbOrder.orderCode,
                        archived: fbOrder.archived,
                        sortOrder: fbOrder.sortOrder
                    }
                )
            }
        );

        let result = await choiceApi.fishbones.createFishbone(fishboneCreatePayload);
        if (result) { // error
            popAlert(result, "Fishbone Create", ModalStyles.warning);
            return;
        }
        await getAllFishboneData();

        let createdFishbone = pageData.fbTypes?.find(fb => fb.description == editState.fishboneCopyName);

        // copy order results
        fishboneCreatePayload = {};
        fishboneCreatePayload.fishboneResults = [];
        getFbOrderResultsBySelectedFb().forEach(item => {
            let orderId = pageData.fbOrders?.find(order =>
                            order.orderCode == item.fbOrderLabel &&
                            order.orderLocationId == item.fbOrderLocationId &&
                            order.fbId == createdFishbone?.fbId 
                        )?.orderId;
            let fbSubId = pageData.fbSubTypes?.find(fbItem => fbItem.label == item.fbItemLabel && fbItem.fbId == createdFishbone?.fbId)?.fbSubId;
            if (!orderId || !fbSubId) {
                return;
            }
            fishboneCreatePayload.fishboneResults?.push({
                orderId: orderId,
                fbSubId: fbSubId,
                fbResultCodeType: item.fbResultCodeType,
                fbResultCode: item.fbResultCode
            });
        });

        result = await choiceApi.fishbones.createFishbone(fishboneCreatePayload);
        editState.fishboneCopyName = undefined;
        if (result) { // error
            popAlert(result, "Fishbone Create", ModalStyles.warning);
            return;
        }
        await getAllFishboneData();

        pageData.selectedFishbone = createdFishbone;
        updateViewedLists();
    }

    function updateViewedLists() {
        pageData.selectedFbSubTypes = pageData.fbSubTypes?.filter((item: IFishboneSubType) => item.fbId == pageData.selectedFishbone?.fbId);
        pageData.selectedFbOrders = pageData.fbOrders?.filter((item: IFishboneOrder) => item.fbId == pageData.selectedFishbone?.fbId);
    }

    function getFbOrderResultsBySelectedFb() {
        let result: IFishboneOrderResultParsed[] = [];
        pageData.fbOrderResults?.forEach(orderResult => {
            let fbOrder = pageData.fbOrders?.find(item => item.orderId == orderResult.orderId);
            let fbItem = pageData.fbSubTypes?.find(item => item.fbSubId == orderResult.fbSubId);

            if (fbOrder?.fbId == pageData.selectedFishbone?.fbId && fbItem?.fbId == pageData.selectedFishbone?.fbId) {
                result.push({
                    fbItemLabel: fbItem?.label,
                    fbOrderLabel: fbOrder?.orderCode,
                    fbOrderLocationId: fbOrder?.orderLocationId,
                    fbResultCodeType: orderResult.fbResultCodeType,
                    fbResultCode: orderResult.fbResultCode
                })
            }
        })
        return result;
    }
</script>

<style>
    #fbPage{
        background-color: #F4F4F4;
        margin: 0 -10px 0 -10px;
        width: 99.9vw;
        height: 99.9vh;
    }
    #fbPage *,
    .fbSearchWrapper > :first-child > :nth-child(2) {
        font-size: small;
    }
    #fbPage h5 {
        font-size: 1.25rem;
    }
    #fbToolbar {
        display: flex;
        gap: 1rem;
        width: 100%;
        background-color: #E7F0FF;
        border-bottom: solid 0.1rem #146AFF;
        justify-content: flex-start;
        margin-bottom: 0;
    }
    #fbToolbar > :first-child {
        margin-left: 1.25rem;
    }
    #fbTitle {
        font-size: x-large;
        color: black;
        padding-top: 0.6rem;
        font-weight: 500;
    }
    #fbTitle, #fbMain {
        margin-left: 1rem;
    }
    #fbMain {
        display: flex;
        width: 98%;
        height: 88%;
        background-color: white;
        border-radius: 0.5rem;
    }
    #fbList {
        display: flex;
        flex-direction: column;
        width: 15%;
        height: 100%;
        padding: 0 1rem;
        border-right: solid 0.2rem #F4F4F4;
    }
    .fbListSearch {
        width: 100%;
        border-bottom: solid 0.2rem #F4F4F4;
    }
    .fbSectionHeader {
        display: flex;
        justify-content: space-between;
    }
    .fbSectionHeaderIcon {
        margin-top: 1.3rem;
        cursor: pointer;
    }
    #fbSubTypeList {
        width: 20%;
        height: 100%;
        padding: 0 1rem;
        border-right: solid 0.2rem #F4F4F4;
    }
    #fbOrdersList {
        width: 20%;
        height: 100%;
        padding: 0 1rem;
        border-right: solid 0.2rem #F4F4F4;
    }
    #fbResultList {
        width: 45%;
        height: 100%;
        padding: 0 1rem;
        border-radius: 0 0.3rem 0.3rem 0;
    }
    .fbListSectionTitle {
        margin-top: 1rem;
        font-weight: bold;
    }
    .fbSearchWrapper {
        margin: 1.5rem 0 2rem 0;
    }
    #fbSubTypeOrderSearchWrapper {
        display: flex;
        flex-direction: row;
        gap: 1rem;
    }
    .fbListResults {
        margin-bottom: 1rem;
        overflow: auto;
        max-height: 66vh;
    }
    .fbListResultsEdit {
        max-height: 56vh;
    }
    .fbListResultsAllSelected {
        max-height: 49.4vh;
    }
    .fbListResultsEditNewItem {
        max-height: 52vh;
    }
    .fbListResultsAllSelectedAndNewItem {
        max-height: 45vh;
    }
    #fbEdit {
        margin: auto 0 1rem 0;
    }
    #fbEdit button:first-child {
        width: 100%;
    }
    .fbListItemWrapper {
        border-top: solid 0.15rem #F4F4F4;
        margin: 0.4rem 0;
    }
    .fbTypeListItem {
        display: flex;
        padding: 0.5rem 0 0.5rem 0.5rem;
        margin-top: 0.4rem;
        color: #0E4AB3;
        font-weight: 500;
        border-radius: 0.5rem;
        cursor: pointer;
    }
    .fbAllCheckboxWrapper {
        margin: 0.5rem 0 0 0.25rem;
        padding: 0.5rem 0 0.5rem 0;
        font-weight: 500;
    }
    .archiveToggle {
        margin: 0 0.25rem 0 0.5rem;
    }
    .fbTypeListItem > :nth-child(2) > :first-child,
    .fbItemWrapper > :nth-child(2) > :first-child,
    .fbAllCheckboxWrapper > :first-child > :first-child {
        margin: 0.1rem 0.5rem 0 0rem;
    }
    .fbTypeListItem > :nth-child(2) > :first-child,
    .fbItemWrapper > :nth-child(2) > :first-child,
    .fbAllCheckboxWrapper > :first-child > :first-child {
        width: 1rem;
        height: 1rem;
        box-shadow: none;
        cursor: pointer;
    }
    .fbListSection {
        height: 66vh;
        overflow: auto;
    }
    .fbListSectionEdit {
        height: 56vh;
        overflow: auto;
    }
    .fbListSectionEditAllSelected {
        height: 50.25vh;
    }
    .fbTypeListItem:hover, .fbItemWrapper:hover {
        color: black;
        background-color: #F3F8FF;
        cursor: pointer;
    }
    .fbSubTypeHeaderWrapper, .fbOrderHeaderWrapper, .fbResultHeaderWrapper {
        display: flex;
        margin: 0.4rem 0;
        padding: 0.5rem 0 0.5rem 0.5rem;
    }
    .fbSubTypeHeaderWrapperEdit, .fbOrderHeaderWrapperEdit {
        gap: 2.4rem;
    }
    #fbSubTypeLabelHeader, #fbSubTypeDescriptionHeader {
        font-weight: bold;
    }
    #fbSubTypeLabelHeader, .fbSubTypeListItemLabel {
        width: 25%;
    }
    #fbSubTypeDescriptionHeader, .fbSubTypeListItemDescription {
        width: 75%;
    }
    .fbItemWrapper, .fbResultWrapper {
        display: flex;
        border-radius: 0.5rem;
        margin-top: 0.4rem;
    }
    .fbResultWrapper {
        padding: 0.5rem 0 0.5rem 0.5rem;
    }
    .fbItemWrapper {
        cursor: pointer;
        padding: 0.5rem 0 0.5rem 0.5rem;
    }
    .fbSubTypeListItemLabel {
        font-weight: 500;
    }
    .fbOrderHeader {
        width: 50%;
        font-weight: bold;
    }
    .fbOrderData {
        width: 50%;
    }
    .fbResultHeader, .fbResultData {
        width: 16.67%;
    }
    .fbResultHeaderOrderCode, .fbResultOrderCode {
        width: 13%;
    }
    .fbResultHeaderResultType, .fbResultType {
        width: 22%;
    }
    .fbHeaderBold {
        font-weight: bold;
    }
    .fbResultHeader {
        font-weight: bold;
    }
    .fbResultHeaderWide, .fbResultDataWide {
        width: 25%;
    }
    .fbSelectedListItem {
        background-color: #F3F8FF;
    }
    .fbArchiveToggleActionWrapper {
        display: flex;
        background-color: #E7F0FF;
        border: solid 0.1rem #146AFF;
        cursor: pointer;
        margin-top: 1rem;
        border-radius: 0.3rem;
        font-weight: 500;
        padding: 0.2rem;
    }
    .fbArchiveToggleActionWrapper > div:nth-child(1){
        color: #146AFF;
        border-right: solid 0.1rem #dddddf;
        flex: 1;
        padding: 0.5rem;
        border-radius: 0.3rem;
    }
    .fbArchiveToggleActionWrapper > div:nth-child(2){
        color: #d9534f;
        padding-left: 0.5rem;
        flex: 1;
        padding: 0.5rem;
        border-radius: 0.3rem;
    }
    .fbArchiveToggleActionWrapper > div:nth-child(1):hover,
    .fbArchiveToggleActionWrapper > div:nth-child(2):hover {
        background-color: #F3F8FF;
    }
    .fishboneItemCreateWrapper {
        display: flex;
        padding: 0.5rem;
        gap: 0.2rem;
    }
    .fishboneItemCreateWrapper > :nth-child(1){
        width: 25%;
    }
    .fishboneItemCreateWrapper > :nth-child(2){
        width: 75%;
    }
    .fishboneOrderCreateWrapper {
        display: flex;
        padding: 0.5rem;
        gap: 0.2rem;
    }
    .fishboneOrderCreateWrapper > :nth-child(1){
        width: 56%;
    }
    .fishboneOrderCreateWrapper > :nth-child(1) > .vs__dropdown-toggle,
    .fbResultType > :nth-child(1) > .vs__dropdown-toggle {
        height: 2.1rem;
    }
    .fishboneOrderCreateWrapper > :nth-child(2){
        width: 44%;
    }
    .fbResultHeaderWrapperEdit, .fbResultWrapperNew {
        margin-left: 1rem;
    }
    .fbResultWrapper, .fbResultWrapperEdit {
        width: 100%;
    }
    .resultAction {
        margin-right: 0.2rem;
        margin-top: 1.15rem;
        cursor: pointer;
    }
    #fbCopyModalText {
        font-size: medium;
        margin: 0.5rem 0 1rem 0;
    }
    .fbIcon {
        margin: 0.9rem 0.2rem 0 0;
    }
</style>

<template>
    <div id="fbPage">
        <Toolbar
            id="fbToolbar"
            :configuration="toolbarConfiguration" 
            :toolbarSize="ToolbarSize.small" 
            :toolbarStyle="ToolbarStyle.outline"
            :toolbarBackground="ToolbarBackground.white"/>
        <!-- <div id="toolbar">
            <i class="fa-solid fa-arrow-rotate-left ms-1 toolbarAction" title="Refresh fishbone list" @click="getAllFishboneData"/>
            <i class="fa-solid fa-save ms-1 toolbarAction" title="Save fishbone changes" @click="saveFishboneChanges"/>
            <i class="fa-regular fa-circle-xmark toolbarAction" title="Delete fishbone item"></i> -->
            <!-- <i class="fa-solid fa-wrench toolbarAction" title="Setup wireframe" @click="openSetup"></i> -->
            <!-- <i class="fa-solid fa-magnifying-glass ms-1 toolbarAction"/> -->
            <!-- <i class="fa-solid fa-circle-plus toolbarAction"></i> -->
        <!-- </div> -->
        <PageTitle security-screen-name="GlobalFishbones">
            <template #title>
                <div id="fbTitle">Global Fishbones</div>
            </template>
            <template #permitted>
                <div id="fbMain">
                    <div id="fbList">
                        <div class="fbListSearch">
                            <div class="fbSectionHeader">
                                <div class="fbListSectionTitle">Fishbone List</div>
                                <div class="fbIcon">
                                    <Icon 
                                        v-if="editState.isEditMode && pageData.selectedFbTab == TabType.enabled"
                                        :icon="IconTypes.Add"
                                        title="Create fishbone"
                                        class="fbSectionHeaderIcon"
                                        @click="createFishbone"
                                    />
                                </div>
                            </div>
                            <div class="fbSearchWrapper">
                                <TextInput
                                    placeholder="Search Fishbone ..."
                                    v-model:textValue="pageData.fbSearch"
                                    @update:text-value="clearOnSearch('fb')"
                                >
                                    <template #LeftAddon>
                                        <em class="fa-solid fa-magnifying-glass ms-1"/>
                                    </template>
                                </TextInput>
                            </div>
                        </div>
                        <TabContainer v-if="editState.isEditMode" v-model="pageData.selectedFbTab" @update:modelValue="clearFbToggleList">
                            <TabItem title="Enabled" :value="TabType.enabled">
                                <div class="fbArchiveToggleActionWrapper" v-if="Object.keys(editState.fbTypeArchiveToggle).length">
                                    <div @click="submitArchiveFishboneToggle">Archive</div>
                                    <div @click="() => { editState.fbTypeArchiveToggle = {} }">Cancel</div>
                                </div>
                                <div class="fbAllCheckboxWrapper" v-if="editState.isEditMode">
                                    <Checkbox v-model="allFishbonesSelectedForToggle" @update:modelValue="toggleAllEnabledFbArchiveState"/>
                                    All Fishbones
                                </div>
                                <div class="fbListResults" 
                                    :class="[{ 'fbListResultsEdit': editState.isEditMode },
                                            { 'fbListResultsAllSelected': Object.keys(editState.fbTypeArchiveToggle).length },
                                            { 'fbListResultsEditNewItem': editState.newFishboneName != undefined },
                                            { 'fbListResultsAllSelectedAndNewItem' : Object.keys(editState.fbTypeArchiveToggle).length && editState.newFishboneName != undefined }]">
                                    <div v-if="pageData.fbTypes">
                                        <VueDraggable v-model="pageData.fbTypes">
                                            <div 
                                                v-for="(item, index) in pageData.fbTypes" 
                                                :key="index"
                                            >
                                                <div 
                                                    v-if="!item.archived && (!pageData.fbSearch || new RegExp(pageData.fbSearch,'i').test(item.description))"
                                                    :class="{ 'fbListItemWrapper': index !== 0 }"
                                                >
                                                    <div
                                                        class="fbTypeListItem"
                                                        :class="{ 'fbSelectedListItem': pageData.selectedFishbone?.fbId == item.fbId }"
                                                        @click="selectFishbone(item)"
                                                    >
                                                        <Icon 
                                                            :icon="IconTypes.DragDrop"
                                                        />
                                                        <Checkbox
                                                            v-if="editState.isEditMode"
                                                            class="archiveToggle"
                                                            :modelValue="editState.fbTypeArchiveToggle[item.fbId] != undefined"
                                                            @click.stop="toggleFbArchiveStatus(item)"
                                                        />
                                                        {{ item.description }}
                                                    </div>
                                                </div> 
                                            </div>
                                        </VueDraggable>
                                    </div>
                                </div>
                                <TextInput ref="newFishboneInput" v-if="editState.newFishboneName != undefined" v-model:text-value="editState.newFishboneName"/>
                            </TabItem>
                            <TabItem title="Archived" :value="TabType.archived">
                                <div class="fbArchiveToggleActionWrapper" v-if="Object.keys(editState.fbTypeArchiveToggle).length">
                                    <div @click="submitArchiveFishboneToggle">Enable</div>
                                    <div @click="() => { editState.fbTypeArchiveToggle = {} }">Cancel</div>
                                </div>
                                <div class="fbAllCheckboxWrapper" v-if="editState.isEditMode">
                                    <Checkbox v-model="allFishbonesSelectedForToggle" @update:modelValue="toggleAllArchivedFbArchiveState"/>
                                    All Fishbones
                                </div>
                                <div class="fbListResults" 
                                    :class="[{ 'fbListResultsEdit': editState.isEditMode },
                                            { 'fbListResultsAllSelected': Object.keys(editState.fbTypeArchiveToggle).length },
                                            { 'fbListResultsEditNewItem': editState.newFishboneName != undefined }]">
                                    <div v-if="pageData.fbTypes">
                                        <VueDraggable v-model="pageData.fbTypes">
                                            <div 
                                                v-for="(item, index) in pageData.fbTypes" 
                                                :key="index"
                                            >
                                                <div 
                                                    v-if="item.archived && (!pageData.fbSearch || new RegExp(pageData.fbSearch,'i').test(item.description))"
                                                    :class="{ 'fbListItemWrapper': index !== 0 }"
                                                >
                                                    <div
                                                        class="fbTypeListItem"
                                                        :class="{ 'fbSelectedListItem': pageData.selectedFishbone?.fbId == item.fbId }"
                                                        @click="selectFishbone(item)"
                                                    >
                                                        <Icon 
                                                            :icon="IconTypes.DragDrop"
                                                        />
                                                        <Checkbox
                                                            v-if="editState.isEditMode"
                                                            class="archiveToggle"
                                                            :modelValue="editState.fbTypeArchiveToggle[item.fbId] != undefined"
                                                            @click.stop="toggleFbArchiveStatus(item)"
                                                        />
                                                        {{ item.description }}
                                                    </div>
                                                </div> 
                                            </div>
                                        </VueDraggable>
                                    </div>
                                </div>
                            </TabItem>
                        </TabContainer>
                        <div v-if="!editState.isEditMode" class="fbListResults">
                            <div v-if="pageData.fbTypes">
                                <div 
                                    v-for="(item, index) in pageData.fbTypes" 
                                    :key="index"
                                >
                                    <div 
                                        v-if="!item.archived && (!pageData.fbSearch || new RegExp(pageData.fbSearch,'i').test(item.description))"
                                        :class="{ 'fbListItemWrapper': index !== 0 }"
                                    >
                                        <div
                                            class="fbTypeListItem"
                                            :class="{ 'fbSelectedListItem': pageData.selectedFishbone?.fbId == item.fbId }"
                                            @click="selectFishbone(item)"
                                        >
                                            {{ item.description }}
                                        </div>
                                    </div> 
                                </div>
                            </div>  
                        </div>
                        <div id="fbEdit">
                            <ActionButtonV2
                                v-if="editState.isEditMode == false"
                                :action="ActionButtonStyle.Edit"
                                :display-style="ButtonDisplayStyle.OutlinePrimary"
                                :performAction="async () => { toggleEditMode() }"
                                text="Edit Mode"
                            />
                            <ActionButtonV2
                                v-if="editState.isEditMode == true"
                                :action="ActionButtonStyle.Edit"
                                :display-style="DisplayStyle.Primary"
                                :performAction="async () => { toggleEditMode() }"
                                text="Exit Edit Mode"
                            />
                        </div>
                    </div>
                    <div id="fbSubTypeList">
                        <div class="fbListSearch">
                            <div class="fbSectionHeader">
                                <div class="fbListSectionTitle">{{pageData.selectedFishbone?.description ?? "Fishbone"}} Items</div>
                                <div class="fbIcon">
                                    <Icon 
                                        v-if="editState.isEditMode && pageData.selectedFbSubTypeTab == TabType.enabled"
                                        :icon="IconTypes.Add"
                                        :title="`Add ${pageData.selectedFishbone?.description ?? 'fishbone'} item`"
                                        class="fbSectionHeaderIcon"
                                        @click="addItem"
                                    />
                                </div>
                            </div>
                            <div class="fbSearchWrapper">
                                <TextInput
                                    placeholder="Search Items ..."
                                    v-model:textValue="pageData.fbSubSearch"
                                    @update:text-value="clearOnSearch('subFb')"
                                >
                                    <template #LeftAddon>
                                        <em class="fa-solid fa-magnifying-glass ms-1"/>
                                    </template>
                                </TextInput>
                            </div>
                        </div>
                        <TabContainer 
                            v-if="editState.isEditMode && (pageData.selectedFishbone || editState.newFishboneName)"
                            v-model="pageData.selectedFbSubTypeTab" @update:modelValue="clearFbSubTypeToggleList"
                        >
                            <TabItem title="Enabled" :value="TabType.enabled">
                                <div class="fbArchiveToggleActionWrapper" v-if="Object.keys(editState.fbSubTypeArchiveToggle).length">
                                    <div @click="submitArchiveFishboneSubTypeToggle">Archive</div>
                                    <div @click="() => { editState.fbSubTypeArchiveToggle = {} }">Cancel</div>
                                </div>
                                <div class="fbAllCheckboxWrapper" v-if="editState.isEditMode && pageData.selectedFishbone">
                                    <Checkbox v-model="allFishboneSubTypesSelectedForToggle" @update:modelValue="toggleAllEnabledFbSubTypeArchiveState"/>
                                    All {{pageData.selectedFishbone?.description }} Items
                                </div>
                                <div class="fbListSectionEdit" 
                                    :class="{'fbListSectionEditAllSelected' : Object.keys(editState.fbSubTypeArchiveToggle).length }"
                                    v-if="pageData.selectedFishbone || editState.newFishboneName"
                                >
                                    <div class="fbSubTypeHeaderWrapper fbSubTypeHeaderWrapperEdit">
                                        <div id="fbSubTypeLabelHeader">Label</div>
                                        <div id="fbSubTypeDescriptionHeader">Description</div>
                                    </div>
                                    <div v-if="pageData.selectedFishbone">
                                        <VueDraggable v-model="pageData.selectedFbSubTypes">
                                            <div
                                                v-for="(item, index) in pageData.selectedFbSubTypes" 
                                                :key="index"
                                            >
                                                <div 
                                                    v-if="!item.archived && (!pageData.fbSubSearch || new RegExp(pageData.fbSubSearch,'i').test(item.label))"
                                                    :class="{ 'fbListItemWrapper': index !== 0 }"
                                                >
                                                    <div 
                                                        class="fbItemWrapper"
                                                        :class="{ 'fbSelectedListItem': pageData.selectedSubType?.fbSubId == item.fbSubId }"
                                                        @click="selectFishboneSubType(item)"
                                                    >
                                                        <Icon 
                                                            :icon="IconTypes.DragDrop"
                                                        />
                                                        <Checkbox
                                                            v-if="editState.isEditMode"
                                                            class="archiveToggle"
                                                            :modelValue="editState.fbSubTypeArchiveToggle[item.fbSubId] != undefined" 
                                                            @click.stop="toggleFbSubTypeArchiveStatus(item)"
                                                        />
                                                        <div class="fbSubTypeListItemLabel">{{ item.label }}</div>
                                                        <div class="fbSubTypeListItemDescription">{{ item.description }}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </VueDraggable>
                                    </div>
                                    <template
                                         v-for="(item, index) in editState.fishboneItemLabelToCreate" 
                                         :key="index"
                                    >
                                        <div class="fishboneItemCreateWrapper" :id="'fbSubType_' + index">
                                            <TextInput
                                                v-model:textValue="editState.fishboneItemLabelToCreate[index]"
                                            />
                                            <TextInput
                                                v-model:textValue="editState.fishboneItemDescriptionToCreate[index]"
                                            />
                                        </div>
                                    </template>
                                </div>
                            </TabItem>
                            <TabItem title="Archived" :value="TabType.archived">
                                <div class="fbArchiveToggleActionWrapper" v-if="Object.keys(editState.fbSubTypeArchiveToggle).length">
                                    <div @click="submitArchiveFishboneSubTypeToggle">Enable</div>
                                    <div @click="() => { editState.fbSubTypeArchiveToggle = {} }">Cancel</div>
                                </div>
                                <div class="fbAllCheckboxWrapper" v-if="editState.isEditMode && pageData.selectedFishbone">
                                    <Checkbox v-model="allFishboneSubTypesSelectedForToggle" @update:modelValue="toggleAllArchivedFbSubTypeArchiveState"/>
                                    All {{pageData.selectedFishbone?.description }} Items
                                </div>
                                <div class="fbListSectionEdit" 
                                    :class="{'fbListSectionEditAllSelected' : Object.keys(editState.fbSubTypeArchiveToggle).length }"
                                    v-if="pageData.selectedFishbone || editState.newFishboneName"
                                >
                                    <div class="fbSubTypeHeaderWrapper fbSubTypeHeaderWrapperEdit">
                                        <div id="fbSubTypeLabelHeader">Label</div>
                                        <div id="fbSubTypeDescriptionHeader">Description</div>
                                    </div>
                                    <VueDraggable v-model="pageData.selectedFbSubTypes">
                                        <div
                                            v-for="(item, index) in pageData.selectedFbSubTypes" 
                                            :key="index"
                                        >
                                            <div 
                                                v-if="item.archived && (!pageData.fbSubSearch || new RegExp(pageData.fbSubSearch,'i').test(item.label))"
                                                :class="{ 'fbListItemWrapper': index !== 0 }"
                                            >
                                                <div 
                                                    class="fbItemWrapper"
                                                    :class="{ 'fbSelectedListItem': pageData.selectedSubType?.fbSubId == item.fbSubId }"
                                                    @click="selectFishboneSubType(item)"
                                                >
                                                    <Icon 
                                                        :icon="IconTypes.DragDrop"
                                                    />
                                                    <Checkbox
                                                        v-if="editState.isEditMode"
                                                        class="archiveToggle"
                                                        :modelValue="editState.fbSubTypeArchiveToggle[item.fbSubId] != undefined" 
                                                        @click.stop="toggleFbSubTypeArchiveStatus(item)"
                                                    />
                                                    <div class="fbSubTypeListItemLabel">{{ item.label }}</div>
                                                    <div class="fbSubTypeListItemDescription">{{ item.description }}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </VueDraggable>
                                </div>
                            </TabItem>
                        </TabContainer>
                        <div class="fbListSection" v-if="pageData.selectedFishbone && !editState.isEditMode">
                            <div class="fbSubTypeHeaderWrapper">
                                <div id="fbSubTypeLabelHeader">Label</div>
                                <div id="fbSubTypeDescriptionHeader">Description</div>
                            </div>
                            <div
                                 v-for="(item, index) in pageData.fbSubTypes?.filter((item: IFishboneSubType) => item.fbId == pageData.selectedFishbone?.fbId)" 
                                 :key="index"
                            >
                                <div 
                                    v-if="!item.archived && (!pageData.fbSubSearch || new RegExp(pageData.fbSubSearch,'i').test(item.label))"
                                    :class="{ 'fbListItemWrapper': index !== 0 }"
                                >
                                    <div 
                                        class="fbItemWrapper"
                                        :class="{ 'fbSelectedListItem': pageData.selectedSubType?.fbSubId == item.fbSubId }"
                                        @click="selectFishboneSubType(item)"
                                    >
                                        <div class="fbSubTypeListItemLabel">{{ item.label }}</div>
                                        <div class="fbSubTypeListItemDescription">{{ item.description }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div id="fbOrdersList">
                        <div class="fbListSearch">
                            <div class="fbSectionHeader">
                                <div class="fbListSectionTitle">{{pageData.selectedFishbone?.description ?? "Fishbone"}} Orders</div>
                                <div class="fbIcon">
                                    <Icon 
                                        v-if="editState.isEditMode && pageData.selectedFbOrderTab == TabType.enabled"
                                        :icon="IconTypes.Add"
                                        :title="`Add ${pageData.selectedFishbone?.description ?? 'fishbone'} order`"
                                        class="fbSectionHeaderIcon"
                                        @click="addOrder"
                                    />
                                </div>
                            </div>
                            <div id="fbSubTypeOrderSearchWrapper">
                                <div class="fbSearchWrapper">
                                    <TextInput
                                        placeholder="Search Lab ..."
                                        v-model:textValue="pageData.labSearch"
                                        @update:text-value="clearOnSearch('order')"
                                    >
                                        <template #LeftAddon>
                                            <em class="fa-solid fa-magnifying-glass ms-1"/>
                                        </template>
                                    </TextInput>
                                </div>
                                <div class="fbSearchWrapper">
                                    <TextInput
                                        placeholder="Search Code ..."
                                        v-model:textValue="pageData.codeSearch"
                                        @update:text-value="clearOnSearch('order')"
                                    >
                                        <template #LeftAddon>
                                            <em class="fa-solid fa-magnifying-glass ms-1"/>
                                        </template>
                                    </TextInput>
                                </div>
                            </div>
                        </div>
                        <TabContainer 
                            v-if="editState.isEditMode && (pageData.selectedFishbone || editState.newFishboneName)" 
                            v-model="pageData.selectedFbOrderTab" @update:modelValue="clearFbOrderToggleList"
                        >
                            <TabItem title="Enabled" :value="TabType.enabled">
                                <div class="fbArchiveToggleActionWrapper" v-if="Object.keys(editState.fbOrderArchiveToggle).length">
                                    <div @click="submitArchiveFishboneOrderToggle">Archive</div>
                                    <div @click="() => { editState.fbOrderArchiveToggle = {} }">Cancel</div>
                                </div>
                                <div class="fbAllCheckboxWrapper" v-if="editState.isEditMode && pageData.selectedFishbone">
                                    <Checkbox v-model="allFishboneOrdersSelectedForToggle" @update:modelValue="toggleAllEnabledFbOrderArchiveStatus"/>
                                    All {{pageData.selectedFishbone?.description }} Orders
                                </div>
                                <div class="fbListSectionEdit" 
                                    :class="{'fbListSectionEditAllSelected' : Object.keys(editState.fbOrderArchiveToggle).length }"
                                    v-if="pageData.selectedFishbone || editState.newFishboneName"
                                >
                                    <div class="fbOrderHeaderWrapper fbOrderHeaderWrapperEdit">
                                        <div class="fbOrderHeader">Lab</div>
                                        <div class="fbOrderHeader">Order Code</div>
                                    </div>
                                    <VueDraggable v-model="pageData.selectedFbOrders">
                                        <div
                                            v-for="(item, index) in pageData.selectedFbOrders"
                                            :key="index"
                                        >
                                            <div
                                                v-if="!item.archived && displayOrder(item)"
                                                :class="{ 'fbListItemWrapper': index !== 0 }"
                                            >
                                                <div 
                                                    class="fbItemWrapper"
                                                    :class="{ 'fbSelectedListItem': pageData.selectedOrder?.orderId == item.orderId }"
                                                    @click="selectOrder(item)"
                                                >
                                                    <Icon 
                                                        :icon="IconTypes.DragDrop"
                                                    />
                                                    <Checkbox
                                                        v-if="editState.isEditMode"
                                                        class="archiveToggle"
                                                        :modelValue="editState.fbOrderArchiveToggle[item.orderId] != undefined"
                                                        @click.stop="toggleFbOrderArchiveStatus(item)"
                                                    />
                                                    <div class="fbOrderData">{{ getOrderLocationDisplayName(item)?.displayName }}</div>
                                                    <div class="fbOrderData">{{ item.orderCode }}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </VueDraggable>
                                    <template
                                         v-for="(item, index) in editState.fishboneOrderLabToCreate" 
                                         :key="index"
                                    >
                                        <div class="fishboneOrderCreateWrapper" :id="'fbOrder_' + index">
                                            <v-select
                                                v-model="editState.fishboneOrderLabToCreate[index]"
                                                :options="pageData.fbOrderLocations"
                                                :reduce="(item:IFishboneOrderLocation) => item.locationId"
                                                label="displayName"
                                            />
                                            <TextInput
                                                v-model:textValue="editState.fishboneOrderCodeToCreate[index]"
                                            />
                                        </div>
                                    </template>
                                </div>
                            </TabItem>
                            <TabItem title="Archived" :value="TabType.archived">
                                <div class="fbArchiveToggleActionWrapper" v-if="Object.keys(editState.fbOrderArchiveToggle).length">
                                    <div @click="submitArchiveFishboneOrderToggle">Enable</div>
                                    <div @click="() => { editState.fbOrderArchiveToggle = {} }">Cancel</div>
                                </div>
                                <div class="fbAllCheckboxWrapper" v-if="editState.isEditMode && pageData.selectedFishbone">
                                    <Checkbox v-model="allFishboneOrdersSelectedForToggle" @update:modelValue="toggleAllArchivedFbOrderArchiveStatus"/>
                                    All {{pageData.selectedFishbone?.description }} Orders
                                </div>
                                <div class="fbListSectionEdit" 
                                    :class="{'fbListSectionEditAllSelected' : Object.keys(editState.fbOrderArchiveToggle).length }"
                                    v-if="pageData.selectedFishbone || editState.newFishboneName"
                                >
                                    <div class="fbOrderHeaderWrapper fbOrderHeaderWrapperEdit">
                                        <div class="fbOrderHeader">Lab</div>
                                        <div class="fbOrderHeader">Order Code</div>
                                    </div>
                                    <VueDraggable v-model="pageData.selectedFbOrders">
                                        <div
                                            v-for="(item, index) in pageData.selectedFbOrders"
                                            :key="index"
                                        >
                                            <div
                                                v-if="item.archived && displayOrder(item)"
                                                :class="{ 'fbListItemWrapper': index !== 0 }"
                                            >
                                                <div 
                                                    class="fbItemWrapper"
                                                    :class="{ 'fbSelectedListItem': pageData.selectedOrder?.orderId == item.orderId }"
                                                    @click="selectOrder(item)"
                                                >
                                                    <Icon 
                                                        :icon="IconTypes.DragDrop"
                                                    />
                                                    <Checkbox
                                                        v-if="editState.isEditMode"
                                                        class="archiveToggle"
                                                        :modelValue="editState.fbOrderArchiveToggle[item.orderId] != undefined"
                                                        @click.stop="toggleFbOrderArchiveStatus(item)"
                                                    />
                                                    <div class="fbOrderData">{{ getOrderLocationDisplayName(item)?.displayName }}</div>
                                                    <div class="fbOrderData">{{ item.orderCode }}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </VueDraggable>
                                </div>
                            </TabItem>
                        </TabContainer>
                        <div class="fbListSection" v-if="pageData.selectedFishbone && !editState.isEditMode">
                            <div class="fbOrderHeaderWrapper">
                                <div class="fbOrderHeader">Lab</div>
                                <div class="fbOrderHeader">Order Code</div>
                            </div>
                            <div
                                 v-for="(item, index) in pageData.fbOrders?.filter((item: IFishboneOrder) => item.fbId == pageData.selectedFishbone?.fbId)"
                                 :key="index"
                            >
                                <div
                                    v-if="!item.archived && displayOrder(item)"
                                    :class="{ 'fbListItemWrapper': index !== 0 }"
                                >
                                    <div 
                                        class="fbItemWrapper"
                                        :class="{ 'fbSelectedListItem': pageData.selectedOrder?.orderId == item.orderId }"
                                        @click="selectOrder(item)"
                                    >
                                        <div class="fbOrderData">{{ getOrderLocationDisplayName(item)?.displayName }}</div>
                                        <div class="fbOrderData">{{ item.orderCode }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div id="fbResultList">
                        <div class="fbSectionHeader">
                            <div class="fbListSectionTitle">{{pageData.selectedFishbone?.description ?? "Fishbone"}} Results</div>
                            <div class="fbIcon">
                                <Icon 
                                    v-if="editState.isEditMode && ((pageData.selectedFishbone || editState.newFishboneName) && pageData.selectedSubType && pageData.selectedOrder)"
                                    :icon="IconTypes.Add"
                                    :title="`Add ${pageData.selectedFishbone?.description ?? 'fishbone'} result`"
                                    class="fbSectionHeaderIcon"
                                    @click="addResult"
                                />
                            </div>
                        </div>
                        <div v-if="pageData.selectedSubType && pageData.selectedOrder">
                            <div class="fbResultHeaderWrapper" :class="{ 'fbResultHeaderWrapperEdit': editState.isEditMode }">
                                <div class="fbResultHeader fbResultHeaderWide">Item Information</div>
                                <div class="fbResultHeader fbResultHeaderWide">Lab</div>
                                <div class="fbHeaderBold fbResultHeaderOrderCode">Order Code</div>
                                <div class="fbHeaderBold fbResultHeaderResultType">Result Type</div>
                                <div class="fbResultHeader">Result Code</div>
                            </div>
                            <div style="height: 76vh; overflow: auto">
                                <div
                                    v-for="(item, index) in pageData.fbOrderResults?.filter((orderResult: IFishboneOrderResult) => orderResult.orderId == pageData.selectedOrder?.orderId && orderResult.fbSubId == pageData.selectedSubType?.fbSubId)"
                                    :key="index"
                                >
                                    <div :class="{ 'fbListItemWrapper': index !== 0 }" style="display: flex;">
                                        <div class="fbIcon">
                                            <Icon 
                                                v-if="editState.isEditMode"
                                                :icon="IconTypes.Delete"
                                                title="Delete fishbone result"
                                                :displayStyle="DisplayStyle.Danger"
                                                class="resultAction"
                                                @click="displayDeleteWarning(item)"
                                            />
                                        </div>
                                        <div class="fbResultWrapper" :class="{ 'fbResultWrapperEdit' : editState.isEditMode }">
                                            <div class="fbResultDataWide">{{ pageData.selectedSubType?.label }} - {{ pageData.selectedSubType?.description }}</div>
                                            <div class="fbResultDataWide">{{ getOrderLocationDisplayNameByResult(item.orderId) }}</div>
                                            <div class="fbResultOrderCode">{{ pageData.selectedOrder.orderCode }}</div>
                                            <div class="fbResultType">{{ item.fbResultCodeType }}</div>
                                            <div class="fbResultData">{{ item.fbResultCode }}</div>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-for="(item, index) in editState.fishboneResultTypeToCreate" 
                                    :key="index"
                                    style="display: flex;"
                                >
                                    <div class="fbResultWrapper fbResultWrapperNew" :id="'result_' + index">
                                        <div class="fbResultDataWide">{{ pageData.selectedSubType?.label }} - {{ pageData.selectedSubType?.description }}</div>
                                        <div class="fbResultDataWide">{{ getOrderLocationDisplayNameByResult(pageData.selectedOrder.orderId) }}</div>
                                        <div class="fbResultOrderCode">{{ pageData.selectedOrder.orderCode }}</div>
                                        <div class="fbResultType">
                                            <v-select
                                                v-model="editState.fishboneResultTypeToCreate[index]"
                                                :options="ResultCodeTypes"
                                                :reduce="(item: IFishboneResultCodeType) => item.value"
                                                label="displayValue"
                                            />
                                        </div>
                                        <div class="fbResultData">
                                            <TextInput v-model:textValue="editState.fishboneResultCodeToCreate[index]"/>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <ModalDialog :show="editState.fishboneCopyName != undefined" :size="ModalSize.Medium" @hide="() => {}">
                    <template #title><h5>Copy Fishbone</h5></template>
                    <div class="mb-1 w-50">
                        <div id="fbCopyModalText">{{'Enter new fishbone name'}}</div>
                        <TextInput
                            v-model:textValue="editState.fishboneCopyName"
                        />
                    </div>
                    <template #footer>
                        <ActionButtonV2
                            :action="ActionButtonStyle.Add"
                            :display-style="ButtonDisplayStyle.OutlinePrimary"
                            :performAction="createFishboneCopy"
                            text="Create"
                        />
                        <ActionButtonV2
                            :action="ActionButtonStyle.Close"
                            :display-style="DisplayStyle.Secondary"
                            :performAction="async () => { editState.fishboneCopyName = undefined }"
                            text="Cancel"
                        />
                    </template>
                </ModalDialog>
            </template>
        </PageTitle>
    </div>
</template>

